'use client';

import { useState, useEffect } from 'react';
import dynamic from 'next/dynamic';
import LoadingSkeleton from './loading-skeleton';

// Dynamically import ApexCharts (no SSR)
const Chart = dynamic(() => import('react-apexcharts'), { ssr: false });

interface ExpiredDevicesChartProps {
  data: Array<{
    count: number;
    date: string; // format: YYYY-MM-DD
  }>;
}

export default function ExpiredDevicesChart({ data = [] }: ExpiredDevicesChartProps) {
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 800);
    return () => clearTimeout(timer);
  }, []);

  // ✅ Aggregate counts by month
  const aggregatedData = (data || []).reduce((acc, d) => {
    const month = d.date.slice(0, 7); // YYYY-MM
    acc[month] = (acc[month] || 0) + d.count;
    return acc;
  }, {} as Record<string, number>);

  const sortedData = Object.entries(aggregatedData)
    .map(([month, count]) => ({ month, count }))
    .sort((a, b) => new Date(a.month).getTime() - new Date(b.month).getTime());

  // detect Tailwind dark mode
  const isDark =
    typeof window !== 'undefined' &&
    document.documentElement.classList.contains('dark');

  const chartOptions = {
    chart: {
      type: 'area' as const,
      height: 250,
      background: 'transparent',
      toolbar: { show: false },
      zoom: { enabled: false },
    },
    stroke: {
      curve: 'smooth' as const,
      width: 2,
    },
    colors: ['#0096FB'],
    fill: {
      type: 'gradient',
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.35,
        opacityTo: 0,
        stops: [0, 100],
      },
    },
    dataLabels: {
      enabled: false,
    },
    markers: {
      size: 0,
      colors: ['#0096FB'],
      strokeColors: isDark ? '#1f2937' : '#fff',
      strokeWidth: 2,
      hover: { size: 5 },
    },
    grid: {
      show: true,
      borderColor: isDark ? '#374151' : '#e2e8f0',
      strokeDashArray: 3,
      xaxis: { lines: { show: false } },
      yaxis: { lines: { show: true } },
    },
    xaxis: {
      axisBorder: { show: false },
      axisTicks: { show: false },
      labels: { style: { colors: '#94a3b8', fontSize: '11px' } },
      categories: sortedData.map((d) => {
        const date = new Date(d.month + '-01');
        return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      }),
    },
    yaxis: {
      min: 0,
      labels: { style: { colors: '#94a3b8', fontSize: '11px' } },
      title: { text: '' },
    },
    tooltip: {
      theme: isDark ? 'dark' : 'light',
    },
  };

  const chartSeries = [
    {
      name: 'Expired Devices',
      data: sortedData.map((d) => d.count),
    },
  ];

  if (isLoading) {
    return <LoadingSkeleton className="h-[250px] w-full" />;
  }

  return (
    <div className="w-full">
      <Chart options={chartOptions} series={chartSeries} type="area" height={250} />
    </div>
  );
}
